import * as ko from "knockout";
import * as Constants from "../common/constants";
import * as Global from "../common/global";
import * as UI from "../common/ui";
import * as CustomerData from "../data/customerData";
import { IController } from "../interfaces/controller";
import * as Data from "../interfaces/data";
import * as Translations from "../interfaces/translations";

interface ICustomerViewModel {
    Name: KnockoutObservable<string>;
    ShortName: KnockoutObservable<string>;
    IsActive: KnockoutObservable<boolean>;
    Users: KnockoutObservableArray<CustomerData.ICustomerEditMasterUser>;
    CustomerLanguages: KnockoutComputed<Translations.ILanguage[]>;
    AllLanguages: KnockoutObservableArray<ICustomerLanguage>;
    LanguageID: KnockoutObservable<number>;
    Errors?: KnockoutValidationErrors;
    OnInactivate: () => void;
    OnActivate: () => void;
    UpdateCustomerSettings?: () => void;
}

interface ICustomerLanguage extends Translations.ILanguage {
    IsSelected: KnockoutObservable<boolean>;
 }

export class CustomerController implements IController {
    // references to DOM elements
    private container: JQuery;
    private viewModel: ICustomerViewModel;
    private customerID: number;
    private fieldsOnLoad: string = "";

    constructor() {
        const me: CustomerController = this;

        this.viewModel = {
            Name: ko.observable<string>().extend({ required: true }),
            ShortName: ko.observable<string>().extend({ required: true }),
            IsActive: ko.observable<boolean>(true),
            Users: ko.observableArray<CustomerData.ICustomerEditMasterUser>([]),
            AllLanguages: ko.observableArray<ICustomerLanguage>([]),
            LanguageID: ko.observable<number>(),

            OnInactivate: () => {
                UI.showConfirmDialog("Are you sure you want to make the customer inactive?",
                    () => { me.toggleIsActive(); });
            },
            OnActivate: () => {
                UI.showConfirmDialog("Are you sure you want to make the customer active?",
                    () => { me.toggleIsActive(); });
            },

            CustomerLanguages: ko.pureComputed((): Translations.ILanguage[] => {
                const customerLanguages: Translations.ILanguage[] = [];
                me.viewModel.AllLanguages().forEach((allLanguage: ICustomerLanguage) => {
                    if (allLanguage.IsSelected() === true) {
                        customerLanguages.push( {ID: allLanguage.ID, Name: allLanguage.Name, Abbreviation: "" } );
                    }

                });

                return customerLanguages;
            }),

        };
        this.viewModel.UpdateCustomerSettings = (): void => {

            const url: string = "/CustomerSettings/" + this.customerID;
            Global.app().router().navigate(url , true);
        };

        this.viewModel.Errors = ko.validation.group(this.viewModel, { deep: true });
    }

    public render(container: JQuery): JQueryPromise<any> {
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();

        this.container = container;

        ko.applyBindings(this.viewModel, container[0]);

        deferred.resolve();
        return deferred.promise();
    }

    public load(params: any[]): JQueryPromise<any> {
        // customer ID must be passed as the first parameter
        if (!params || params.length === 0) {
            // no ID passed, close page
            Global.app().pageManager().closeCurrentPage();
            const deferred: JQuery.Deferred<any, any, any> = $.Deferred();
            deferred.resolve();
            return deferred.promise();
        }

        this.customerID = params[0];
        return this.loadRecord(this.customerID);
    }

    public haveFieldsChanged(): boolean {
        const fieldValues: string = JSON.stringify(this.createCustomerEditMaster());
        return fieldValues !== this.fieldsOnLoad;
    }

    public appRouterID(params: any[]): string {
        let appRouterID: string = "Customer";
        if (params != null && params.length === 1) {
            appRouterID += params[0];
        }
        return appRouterID;
    }

    public save(): JQueryPromise<any> {
        let waitContainer: JQuery;
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();

        if (this.isValid()) {
            waitContainer = UI.showWaitIndicator(this.container);
            CustomerData.SaveCustomer(this.createCustomerEditMaster())
                .done((response: Data.IResponse<CustomerData.ICustomerEditMaster>) => {
                    if (response.Success) {
                        this.updateViewModel(response.Payload);
                        UI.showNotificationMessage(Constants.PHRASE_RECORDSAVED, UI.messageTypeValues.success);
                        deferred.resolve();
                    } else {
                        UI.showErrorMessage(response.ErrorMessages);
                        deferred.reject();
                    }
                })
                .fail(() => {
                    deferred.reject();
                })
                .always(() => {
                    UI.hideWaitIndicator(this.container, waitContainer);
                });
        } else {
            // not all valid fields are entered
            deferred.reject();
        }

        return deferred.promise();
    }

    // returns a title for the page
    public title(): string {
        return "Customer";
    }

    public subTitle(): string {
        return this.viewModel.Name();
    }

    private loadRecord(customerID: number): JQueryPromise<any> {
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();
        const waitContainer: JQuery<HTMLElement> = UI.showWaitIndicator(this.container);

        CustomerData.GetCustomer(this.customerID)
            .done((response: Data.IResponse<CustomerData.ICustomerEditMaster>) => {
                if (response.Success) {
                    this.updateViewModel(response.Payload);
                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                }
            })
            .always(() => {
                UI.hideWaitIndicator(this.container, waitContainer);
                deferred.resolve();
            });

        return deferred.promise();
    }

    private isValid(): boolean {
        let isValid: boolean = true;

        if (this.viewModel.Errors &&
            this.viewModel.Errors().length > 0) {
            this.viewModel.Errors.showAllMessages();
            UI.showErrorMessage([Constants.PHRASE_INVALIDFIELDSONSAVE]);
            isValid = false;
        }

        return isValid;
    }

    private toggleIsActive(): void {
        const waitContainer: JQuery<HTMLElement> = UI.showWaitIndicator(this.container);
        const request: CustomerData.ICustomerChangeIsActiveRequest = {
            CustomerID: this.customerID,
            IsActive: !this.viewModel.IsActive(),
        };
        CustomerData.ChangeIsActive(request)
            .done((response: Data.IResponse<any>) => {
                if (response.Success) {
                    this.updateViewModel(response.Payload);
                    UI.showNotificationMessage(Constants.PHRASE_RECORDSAVED, UI.messageTypeValues.success);
                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                }
            })
            .always(() => {
                UI.hideWaitIndicator(this.container, waitContainer);
            });

    }

    private updateViewModel(customerMaster: CustomerData.ICustomerEditMaster) {
        const customerLanguages: ICustomerLanguage[] = [];

        customerMaster.AllLanguages.forEach((allLanguage) => {
            const isSelected = ko.observable(customerMaster.CustomerLanguages.filter((x) => x.ID === allLanguage.ID).length > 0);
            const customerLanguage: ICustomerLanguage = {
                ID: allLanguage.ID,
                Name: allLanguage.Name,
                IsSelected: isSelected,
                Abbreviation: "",
            };
            customerLanguages.push(customerLanguage);
        });
        this.viewModel.AllLanguages(customerLanguages);
        this.viewModel.Name(customerMaster.Name);
        this.viewModel.ShortName(customerMaster.ShortName);
        this.viewModel.IsActive(customerMaster.IsActive);
        this.viewModel.Users(customerMaster.Users || []);
        this.viewModel.LanguageID(customerMaster.LanguageID);
        this.fieldsOnLoad = JSON.stringify(this.createCustomerEditMaster());
    }

    private createCustomerEditMaster(): CustomerData.ICustomerEditMaster {
        const customerEditMaster: CustomerData.ICustomerEditMaster = {
            ID: this.customerID,
            Name: this.viewModel.Name(),
            ShortName: this.viewModel.ShortName(),
            IsActive: this.viewModel.IsActive(),
            CustomerLanguages: this.viewModel.CustomerLanguages(),
            LanguageID: this.viewModel.LanguageID(),
            AllLanguages: this.viewModel.AllLanguages(),
        };
        return customerEditMaster;
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--7!./src/script/controllers/customer.ts